import type { InteractionEvent } from '../../interaction/types';
import type { CprAngleService } from '../../services/cpr-angle-service';
import { CprViewport2D } from '../../viewports';
import type { IViewport } from '../../viewports/types';
import type { ITool } from '../types';

/**
 * Angle Scroll Tool — adjusts the CPR angle in 2D viewports using the mouse wheel.
 *
 * Publishes angle changes through CprAngleService so the AngleSlider and
 * CprViewport2D stay in sync via the standard PubSub mechanism.
 *
 * Behavior:
 * - Scroll up   → increase angle (+1)
 * - Scroll down → decrease angle (-1)
 *
 * Notes:
 * - Only active on CprViewport2D viewports.
 * - Each wheel tick modifies the angle by exactly one unit.
 * - Stateless tool (no drag lifecycle, nothing to cancel).
 */
export class AngleScrollTool implements ITool {
    static readonly toolName = 'angle-scroll';

    readonly id = AngleScrollTool.toolName;
    readonly name = 'Angle Scroll';
    readonly cursor = 'default';
    readonly completed = false;

    private viewport: IViewport | null = null;

    constructor(private readonly cprAngleService: CprAngleService) {}

    activate(viewport: IViewport): void {
        this.viewport = viewport;
    }

    deactivate(): void {
        this.viewport = null;
    }

    cancel(): void {
        // Nothing to cancel for wheel-only tool
    }

    handleEvent(event: InteractionEvent): boolean {
        if (event.type !== 'wheel' || event.deltaY === undefined) {
            return false;
        }

        if (!(this.viewport instanceof CprViewport2D)) {
            return false;
        }

        const direction = event.deltaY > 0 ? -1 : 1;
        this.cprAngleService.setCurrentAngle(this.cprAngleService.getCurrentAngle() + direction);
        return true;
    }
}
